import { UTM } from "./config";
import { sanitizeUrl } from "./masking";
import type { TouchInfo } from "./types";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
type UtmKey = typeof UTM_KEYS[number];

// Click-id params we capture (for attribution) AND strip from the URL bar so
// social/SEO crawlers and back/refresh don't propagate them.
const CLICK_ID_PARAMS = ["fbclid", "gclid", "msclkid", "ttclid", "yclid", "twclid"] as const;

export interface UtmValues {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
  // Click IDs preserved on the attribution context (not stripped from values).
  gclid: string | null;
  fbclid: string | null;
}

const EMPTY_UTM: UtmValues = {
  source: null, medium: null, campaign: null, term: null, content: null,
  gclid: null, fbclid: null,
};

function readQuery(): URLSearchParams | null {
  if (typeof window === "undefined") return null;
  try {
    return new URL(window.location.href).searchParams;
  } catch {
    return null;
  }
}

function truncate(v: string | null): string | null {
  if (!v) return v;
  return v.length > UTM.max_value_len ? v.slice(0, UTM.max_value_len) : v;
}

function parseFromQuery(): UtmValues {
  const q = readQuery();
  if (!q) return EMPTY_UTM;
  const get = (k: UtmKey) => truncate(q.get(k));
  return {
    source: get("utm_source"),
    medium: get("utm_medium"),
    campaign: get("utm_campaign"),
    term: get("utm_term"),
    content: get("utm_content"),
    gclid: truncate(q.get("gclid")),
    fbclid: truncate(q.get("fbclid")),
  };
}

function inferFromReferrer(): UtmValues {
  if (typeof document === "undefined" || !document.referrer) return EMPTY_UTM;
  try {
    const ref = new URL(document.referrer);
    const host = ref.hostname.replace(/^www\./, "");
    if (host.endsWith("nickrobotic.com")) return EMPTY_UTM;
    const knownEngines: Record<string, string> = {
      "google.com": "google",
      "bing.com": "bing",
      "duckduckgo.com": "duckduckgo",
      "yahoo.com": "yahoo",
      "ecosia.org": "ecosia",
      "brave.com": "brave",
    };
    const sourceKey = Object.keys(knownEngines).find((k) => host === k || host.endsWith(`.${k}`));
    if (sourceKey) return { ...EMPTY_UTM, source: knownEngines[sourceKey], medium: "organic" };
    return { ...EMPTY_UTM, source: host, medium: "referral" };
  } catch {
    return EMPTY_UTM;
  }
}

function readJson<T>(storage: Storage, key: string): T | null {
  try {
    const v = storage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(storage: Storage, key: string, val: unknown): void {
  try {
    storage.setItem(key, JSON.stringify(val));
  } catch {
    /* quota / unavailable */
  }
}

/** Strip utm_* + click-id params from the current URL via replaceState. */
function stripUrlParams(): void {
  if (typeof window === "undefined" || typeof history === "undefined") return;
  try {
    const url = new URL(window.location.href);
    let mutated = false;
    for (const k of [...UTM_KEYS, ...CLICK_ID_PARAMS]) {
      if (url.searchParams.has(k)) {
        url.searchParams.delete(k);
        mutated = true;
      }
    }
    if (mutated) {
      const newUrl = url.pathname + (url.search ? url.search : "") + url.hash;
      history.replaceState(history.state, "", newUrl);
    }
  } catch {
    /* noop */
  }
}

let initialized = false;
let cachedUtm: UtmValues = EMPTY_UTM;

export function initUtm(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  const fromQuery = parseFromQuery();
  const fromReferrer = inferFromReferrer();
  // Use query if any UTM or click-id present, else infer from referrer.
  const hasQuerySignal =
    !!(fromQuery.source || fromQuery.gclid || fromQuery.fbclid);
  const utm = hasQuerySignal ? fromQuery : fromReferrer;
  cachedUtm = utm;

  // session-scoped UTM (for session_started enrichment)
  writeJson(sessionStorage, UTM.session_key, utm);

  if (utm.source || utm.gclid || utm.fbclid) {
    const touch: TouchInfo = {
      source: utm.source,
      medium: utm.medium,
      campaign: utm.campaign,
      referrer: sanitizeUrl(typeof document !== "undefined" ? document.referrer : null, UTM.strip_params),
      landed_at: new Date().toISOString(),
    };
    // first-touch — only set if absent
    if (!readJson<TouchInfo>(localStorage, UTM.first_touch_key)) {
      writeJson(localStorage, UTM.first_touch_key, touch);
    }
    // last-touch — always overwrite
    writeJson(localStorage, UTM.last_touch_key, touch);
  }

  // Strip query params from the URL bar so they don't propagate via
  // bookmarks, back-button, or social shares. Attribution is already
  // persisted in storage above.
  stripUrlParams();
}

export function getUtm(): UtmValues {
  if (!initialized) initUtm();
  return cachedUtm;
}

export function getFirstTouch(): TouchInfo | null {
  if (typeof window === "undefined") return null;
  return readJson<TouchInfo>(localStorage, UTM.first_touch_key);
}

export function getLastTouch(): TouchInfo | null {
  if (typeof window === "undefined") return null;
  return readJson<TouchInfo>(localStorage, UTM.last_touch_key);
}

/** Full attribution context to attach to high-value conversion events. */
export function getAttributionContext(): {
  first: TouchInfo | null;
  last: TouchInfo | null;
  session_utm: UtmValues;
} {
  return { first: getFirstTouch(), last: getLastTouch(), session_utm: cachedUtm };
}

export function resetTouches(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(UTM.first_touch_key);
    localStorage.removeItem(UTM.last_touch_key);
    sessionStorage.removeItem(UTM.session_key);
  } catch {
    /* noop */
  }
  cachedUtm = EMPTY_UTM;
}
